import React, { useState } from "react";
import { Path, Warning, CaretDown, TrafficCone, ArrowSquareOut } from "@phosphor-icons/react";
import { relTime, formatDate } from "../lib/incidents";
import { usePersistentState } from "../lib/usePersistentState";

const PRIORITY_META = {
  Highest: { color: "#E63946", label: "HIGHEST" },
  High: { color: "#F2542D", label: "HIGH" },
  Medium: { color: "#FFB347", label: "MED" },
  Low: { color: "#94A39A", label: "LOW" },
};

const CATEGORY_ICON = {
  Closure: Warning,
  Construction: TrafficCone,
  Collision: Warning,
  default: Path,
};

function RoadAlertRow({ alert }) {
  const [open, setOpen] = useState(false);
  const meta = PRIORITY_META[alert.priority] || { color: "#94A39A", label: alert.priority || "—" };
  const Icon = CATEGORY_ICON[alert.category] || CATEGORY_ICON.default;
  const hasExtra = !!(alert.description || alert.start_time || alert.end_time || alert.start_lat);
  const mapsUrl = alert.start_lat && alert.start_lon
    ? `https://www.google.com/maps?q=${alert.start_lat},${alert.start_lon}`
    : null;

  return (
    <li data-testid={`road-alert-${alert.id}`} className="hover:bg-tac-surface/60 transition-colors">
      <button
        data-testid={`road-alert-toggle-${alert.id}`}
        onClick={() => hasExtra && setOpen((v) => !v)}
        className={`w-full text-left px-4 py-2.5 flex items-start gap-2.5 ${hasExtra ? "cursor-pointer" : "cursor-default"}`}
      >
        <Icon size={14} weight="fill" className="text-tac-dim shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span
              className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border"
              style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}15` }}
            >
              {meta.label}
            </span>
            <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-faint">
              {alert.category} · {alert.road ? `SR ${alert.road}` : "—"}
              {alert.start_milepost != null
                ? ` · MP ${alert.start_milepost}${
                    alert.end_milepost != null && alert.end_milepost !== alert.start_milepost
                      ? `–${alert.end_milepost}`
                      : ""
                  }`
                : ""}
            </span>
            {alert.last_updated && (
              <span className="text-[15px] font-mono-tac text-tac-faint ml-auto">
                {relTime(alert.last_updated)}
              </span>
            )}
          </div>
          <div className={`text-xs text-tac-text leading-snug ${open ? "" : "line-clamp-2"}`}>
            {alert.headline}
          </div>
          {hasExtra && (
            <div className="flex items-center gap-1.5 mt-1 text-[15px] font-bold uppercase tracking-wider text-tac-dim">
              <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
              {open ? "Less" : "More"}
            </div>
          )}
        </div>
      </button>

      {open && hasExtra && (
        <div data-testid={`road-alert-detail-${alert.id}`} className="px-4 pb-3 pl-10 space-y-2 text-[11px]">
          {alert.description && alert.description !== alert.headline && (
            <p className="text-tac-dim leading-relaxed whitespace-pre-line">{alert.description}</p>
          )}
          <dl className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1 font-mono-tac text-[10px]">
            {alert.status && (
              <>
                <dt className="text-tac-faint uppercase tracking-wider">Status</dt>
                <dd className="text-tac-text">{alert.status}</dd>
              </>
            )}
            {alert.county && (
              <>
                <dt className="text-tac-faint uppercase tracking-wider">County</dt>
                <dd className="text-tac-text">{alert.county}</dd>
              </>
            )}
            {alert.start_time && (
              <>
                <dt className="text-tac-faint uppercase tracking-wider">Begins</dt>
                <dd className="text-tac-text">{formatDate(alert.start_time)}</dd>
              </>
            )}
            {alert.end_time && (
              <>
                <dt className="text-tac-faint uppercase tracking-wider">Ends</dt>
                <dd className="text-tac-text">{formatDate(alert.end_time)}</dd>
              </>
            )}
            {alert.start_lat && alert.start_lon && (
              <>
                <dt className="text-tac-faint uppercase tracking-wider">Location</dt>
                <dd className="text-tac-text tabular">
                  {alert.start_lat.toFixed(4)}, {alert.start_lon.toFixed(4)}
                  {alert.end_lat && alert.end_lon && (alert.end_lat !== alert.start_lat || alert.end_lon !== alert.start_lon) && (
                    <span className="text-tac-faint"> → {alert.end_lat.toFixed(4)}, {alert.end_lon.toFixed(4)}</span>
                  )}
                </dd>
              </>
            )}
          </dl>
          {mapsUrl && (
            <a
              data-testid={`road-alert-map-link-${alert.id}`}
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
            >
              <ArrowSquareOut size={10} weight="bold" /> Open in Maps
            </a>
          )}
        </div>
      )}
    </li>
  );
}

export default function RoadAlertsPanel({ alerts }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = usePersistentState("okwatch:panel:roads:collapsed:v2", true);
  if (!alerts || alerts.length === 0) return null;

  const top = alerts.slice(0, open ? alerts.length : 3);

  return (
    <div data-testid="road-alerts-panel" className="border-b border-tac-border bg-tac-surface/40">
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-tac-border/60">
        <button
          data-testid="road-alerts-header"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 group flex-1 min-w-0 text-left"
        >
          <CaretDown
            size={11}
            weight="bold"
            className={`text-tac-faint group-hover:text-tac-text transition-transform shrink-0 ${collapsed ? "-rotate-90" : ""}`}
          />
          <Path size={14} weight="duotone" className="text-tac-warning shrink-0" />
          <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint group-hover:text-tac-text">
            WSDOT Road Alerts
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-dim">· {alerts.length}</span>
        </button>
        {!collapsed && alerts.length > 3 && (
          <button
            data-testid="road-alerts-toggle"
            onClick={() => setOpen(!open)}
            className="text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text flex items-center gap-1 shrink-0"
          >
            {open ? "Show less" : `+${alerts.length - 3} more`}
            <CaretDown size={10} className={open ? "rotate-180" : ""} />
          </button>
        )}
      </div>
      {!collapsed && (
        <ul className="divide-y divide-tac-border/60">
          {top.map((a) => <RoadAlertRow key={a.id} alert={a} />)}
        </ul>
      )}
    </div>
  );
}
